import type {PaymentMethod} from '@frontend/modules/private/advance_payments/AdvancePaymentsModuleTypes'
import type {
    CrmAdvancePaymentRequest,
    CrmAdvancePaymentRequestProduct,
    CrmAdvancePaymentRequestProposedProduct,
    UserDataForAdvancePayment,
} from '@frontend/types/User'
import {
    type DateTimeInstance,
    DateTimeService,
} from 'swayok-react-mdb-ui-kit/services/DateTimeService'

// Минимальная сумма оплаты в зависимости от платежной системы.
const minPaymentAmount: Record<PaymentMethod, number> = {
    // VN:
    momo: 100000,
    baokim: 10000,
    zalo: 10000,
    bank_transfer: 10000,
    // MX:
    conekta: 20,
}

// Максимальная сумма оплаты в зависимости от платежной системы.
const maxPaymentAmount: Record<PaymentMethod, number> = {
    // VN:
    momo: 10000000,
    baokim: 10000000,
    zalo: 10000000,
    bank_transfer: 10000000,
    // MX:
    conekta: 100000,
}

// Работа с заявками на авансовые выплаты.
export abstract class CrmClientHelper {

    // Поиск актуальной заявки на аванс.
    static findActualAdvanceRequest(user: UserDataForAdvancePayment): CrmAdvancePaymentRequest | null {
        if (!user.details.requests) {
            return null
        }

        const requests: CrmAdvancePaymentRequest[] = user.details.requests
        // Проверяем есть ли активный контракт.
        const activeContract = requests.find(
            request => request.calculatedStatus === 'issued'
        )

        if (activeContract) {
            return activeContract
        } else {
            // Выбор последней созданной заявки.
            if (requests.length) {
                return requests.reduce(
                    (accumulator, request) =>
                        (Date.parse(request.createdAt) > Date.parse(accumulator.createdAt)
                            ? request
                            : accumulator)
                )
            }
        }
        return null
    }

    // Найти продукт с наибольшей суммой.
    static getProposedAdvancePaymentProductWithMaxAmount(
        request: CrmAdvancePaymentRequest | null,
        user: UserDataForAdvancePayment
    ): CrmAdvancePaymentRequestProposedProduct | null {
        let products: CrmAdvancePaymentRequestProposedProduct[] = request?.proposedProducts ?? []
        if (!products.length) {
            // Если у активной заявки нет предлагаемых кредитных продуктов, то ищем заявки с таковыми.
            products = this.findAdvanceRequestWithProposedProducts(user)?.proposedProducts ?? []
            if (!products.length) {
                return null
            }
        }
        // Выбираем продукт с наибольшей суммой
        return products.reduce(
            (accumulator, request): CrmAdvancePaymentRequestProposedProduct =>
                (request.amount > accumulator.amount ? request : accumulator)
        ) || null
    }

    // Поиск самой свежей заявки со списком предлагаемых продуктов.
    static findAdvanceRequestWithProposedProducts(
        user: UserDataForAdvancePayment
    ): CrmAdvancePaymentRequest | null {
        let requests: CrmAdvancePaymentRequest[] = user.details.requests
        if (!requests?.length) {
            return null
        }
        // Убираем заявки без продуктов.
        requests = requests.filter(request => request.proposedProducts?.length)
        if (requests.length) {
            return requests.reduce((accumulator, request): CrmAdvancePaymentRequest =>
                (Date.parse(request.createdAt) > Date.parse(accumulator.createdAt)
                    ? request
                    : accumulator))
        }

        return null
    }

    // Получение общего срока займа.
    static getAdvancePaymentPeriodForProduct(
        product: CrmAdvancePaymentRequestProposedProduct | CrmAdvancePaymentRequestProduct
    ): number {
        return product.trancheDuration * product.tranchesCount
    }

    // Получение даты возврата займа.
    static getRepaymentDateForProduct(
        product: CrmAdvancePaymentRequestProposedProduct | CrmAdvancePaymentRequestProduct
    ): DateTimeInstance {
        return DateTimeService.now().add(this.getAdvancePaymentPeriodForProduct(product), 'days')
    }

    // Получить количество дней до возврата займа.
    static getDaysLeftUntilRepaymentDate(repaymentDate: string): number {
        return Math.max(DateTimeService.utc(repaymentDate).diff(DateTimeService.utc(), 'days'))
    }

    // Получить минимальную и максимальную сумму платежа для способа оплаты.
    static getMinMaxPaymentAmounts(paymentMethod: PaymentMethod): {min: number; max: number;} {
        return {
            min: minPaymentAmount[paymentMethod],
            max: maxPaymentAmount[paymentMethod],
        }
    }
}
